import {
	CheckboxGroup,
	CheckboxGroupItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	ValidationError
} from "client/Components";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { IValueInputAttachable } from "client/Forms/attachable";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import { hasMinimumEntries } from "client/Validation/rules";
import { ReactElement } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { OperatingSystem } from "client/Api/common";

export type OperatingSystemsValue = {
	restrictOperatingSystems: boolean;
	operatingSystems: readonly OperatingSystem[];
};

export type OperatingSystemsProps = {
	attachable: IValueInputAttachable<OperatingSystemsValue>;
};

export const OperatingSystems = (props: OperatingSystemsProps): ReactElement => {
	const {
		attachable
	} = props;

	return (
		<div>
			<NoUniqueInputIdContext>
				<RadioButtonGroup
					{...attachable.for("restrictOperatingSystems").attachToRadioButtonGroup()}
					options={restrictOperatingSystemOptions}
				/>

				{attachable.for("restrictOperatingSystems").value && (
					<>
						<CheckboxGroup
							{...attachable.for("operatingSystems").attachToCheckboxGroup()}
							options={operatingSystemOptions}
						/>
						<ValidationError {...attachable.for("operatingSystems").attachToValidationError()} />
					</>
				)}
			</NoUniqueInputIdContext>
		</div>
	);
};
OperatingSystems.displayName = "OperatingSystems";

const restrictOperatingSystemOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		text: localize("_AllOperatingSystems"),
		value: false
	},
	{
		text: localize("_SelectOperatingSystems"),
		value: true
	}
];

const operatingSystemOptions: readonly CheckboxGroupItem<OperatingSystem>[] = [
	{
		text: localize("_Android") + "/" + localize("_ChromeOs"),
		value: OperatingSystem.AndroidChromeOs
	},
	{
		text: localize("_IOs"),
		value: OperatingSystem.IOs
	},
	{
		text: localize("_Windows"),
		value: OperatingSystem.Windows
	},
	{
		text: localize("_MacOs"),
		value: OperatingSystem.MacOs
	},
	{
		text: localize("_Linux") + "/" + localize("_OtherUnixCompatibleSystem"),
		value: OperatingSystem.UnixLinux
	}
];

export function createOperatingSystemsValidation<TModel extends OperatingSystemsValue>(): IValidationConfiguration<TModel> {
	const result: IValidationConfiguration<TModel> = createValidationConfiguration<TModel>();

	const conditionalValidator: IValidationConfiguration<TModel> = result.when(x => x.restrictOperatingSystems);

	const operatingSystemsValidator = conditionalValidator.forCollection("operatingSystems");

	operatingSystemsValidator.add(
		hasMinimumEntries(
			1,
			localize("_AtLeastOneOperatingSystemMustBeSelected")
		)
	);

	return result;
}